import { apply, endTurn, legal, score, startTurnFlip, } from '../engine/index.js';
import { MAPS, SPACE_IDS } from '../maps/index.js';
import { AI_WEIGHTS } from './weights.js';
export * from './weights.js';
const bankCount = (state, index) => [1, 2, 3, 4].reduce((sum, rank) => sum + state.players[index].bank[rank], 0);
export function evaluate(state, me) {
    const opponent = me === 0 ? 1 : 0;
    const breakdown = score(state);
    let value = AI_WEIGHTS.scoreDifference * (breakdown.total[me] - breakdown.total[opponent]);
    for (const region of breakdown.regions) {
        if (region.bonusTo === null)
            continue;
        const [mine, theirs] = [region.controlledSpaces[me], region.controlledSpaces[opponent]];
        const magnitude = Math.abs(mine - theirs) >= 2 ? 1.5 : 1;
        value += AI_WEIGHTS.regionLead * (region.bonusTo === me ? magnitude : -magnitude);
    }
    for (const spaceId of SPACE_IDS) {
        const top = state.board[spaceId].at(-1);
        if (!top || top.side !== state.players[me].side)
            continue;
        if (top.rank >= 3)
            value += AI_WEIGHTS.sturdyTop;
        if (top.rank === 1)
            value += AI_WEIGHTS.fragileTop;
    }
    value += AI_WEIGHTS.bankTempo * (bankCount(state, me) - bankCount(state, opponent));
    if (state.phase === 'gameOver' && state.result) {
        if (state.result.winner === me)
            value += 1_000;
        else if (state.result.winner === opponent)
            value -= 1_000;
    }
    return value;
}
export function allLegalActions(state, includePay = true) {
    const legality = legal(state);
    const actions = [];
    for (const rank of legality.placeableRanks) {
        for (const spaceId of legality.placeTargets(rank))
            actions.push({ type: 'place', rank, spaceId });
    }
    if (includePay)
        for (const rank of legality.payableRanks)
            actions.push({ type: 'pay', rank });
    for (const from of legality.moveSources) {
        for (const to of legality.moveTargets(from))
            actions.push({ type: 'move', from, to });
    }
    for (const spaceId of legality.captureTargets)
        actions.push({ type: 'capture', spaceId });
    return actions;
}
function applied(state, action) {
    const result = apply(state, action);
    return 'error' in result ? null : result.state;
}
function gaussian(rng) {
    const u = Math.max(Number.EPSILON, rng());
    const v = Math.max(Number.EPSILON, rng());
    return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
}
export function choosePenny(state, rng) {
    const me = state.current;
    const actions = [];
    let cursor = state;
    while (cursor.phase === 'actions') {
        const choices = allLegalActions(cursor, false);
        if (choices.length === 0 || rng() < 0.3)
            break;
        const action = choices[Math.floor(rng() * choices.length)];
        if (!action)
            break;
        const next = applied(cursor, action);
        if (!next)
            break;
        actions.push(action);
        cursor = next;
    }
    return { actions, endTurn: true, evaluated: evaluate(cursor, me), searched: actions.length };
}
export function chooseNick(state, rng, options = {}) {
    const me = state.current;
    const sigma = options.noiseSigma ?? 0.75;
    const actions = [];
    let cursor = state;
    let searched = 0;
    while (cursor.phase === 'actions') {
        const currentValue = evaluate(cursor, me);
        let best = null;
        for (const action of allLegalActions(cursor, options.includePay ?? true)) {
            const next = applied(cursor, action);
            if (!next)
                continue;
            searched += 1;
            const value = evaluate(next, me) + (sigma > 0 ? gaussian(rng) * sigma : 0);
            if (!best || value > best.value)
                best = { action, state: next, value };
        }
        if (!best || best.value <= currentValue)
            break;
        actions.push(best.action);
        cursor = best.state;
    }
    return { actions, endTurn: true, evaluated: evaluate(cursor, me), searched };
}
function stateHash(state) {
    return JSON.stringify({
        board: state.board,
        banks: state.players.map((player) => player.bank),
        hands: state.players.map((player) => player.hand.map((coin) => [coin.rank, coin.flipped])),
        entitlements: state.entitlements,
        phase: state.phase,
    });
}
export function enumerateMaximalSequences(state, me, cap = 5_000) {
    let frontier = [{ state, actions: [], heuristic: evaluate(state, me) }];
    const finished = [];
    const seen = new Map();
    while (frontier.length > 0) {
        const nextFrontier = [];
        for (const node of frontier) {
            const choices = node.state.phase === 'actions' ? allLegalActions(node.state) : [];
            if (choices.length === 0) {
                finished.push(node);
                continue;
            }
            let extended = false;
            for (const action of choices) {
                const next = applied(node.state, action);
                if (!next)
                    continue;
                extended = true;
                const heuristic = evaluate(next, me);
                const hash = stateHash(next);
                const previous = seen.get(hash);
                if (previous !== undefined && previous >= heuristic)
                    continue;
                seen.set(hash, heuristic);
                nextFrontier.push({ state: next, actions: [...node.actions, action], heuristic });
            }
            if (!extended)
                finished.push(node);
        }
        nextFrontier.sort((a, b) => b.heuristic - a.heuristic);
        frontier = nextFrontier.slice(0, cap);
        if (finished.length > cap)
            finished.sort((a, b) => b.heuristic - a.heuristic).splice(cap);
    }
    return finished.length > 0 ? finished : [{ state, actions: [], heuristic: evaluate(state, me) }];
}
function flipOutcomeRng(side, mask) {
    let index = 0;
    return () => {
        const matches = (mask & (1 << index++)) !== 0;
        const resultSide = matches ? side : side === 'gold' ? 'silver' : 'gold';
        return resultSide === 'gold' ? 0.25 : 0.75;
    };
}
function opponentExpectation(afterMyTurn, me) {
    if (afterMyTurn.phase === 'gameOver')
        return 0;
    const opponent = afterMyTurn.current;
    const side = afterMyTurn.players[opponent].side;
    const ranks = [1, 2, 3, 4].filter((rank) => afterMyTurn.players[opponent].bank[rank] > 0);
    const outcomes = 1 << ranks.length;
    let sum = 0;
    for (let mask = 0; mask < outcomes; mask += 1) {
        const flipped = startTurnFlip(afterMyTurn, flipOutcomeRng(side, mask)).state;
        const reply = chooseNick(flipped, () => 0.5, { noiseSigma: 0 });
        let replied = flipped;
        for (const action of reply.actions) {
            const next = applied(replied, action);
            if (!next)
                break;
            replied = next;
        }
        sum += evaluate(replied, opponent);
    }
    return sum / outcomes;
}
export function chooseQuinn(state, rng, options = {}) {
    const me = state.current;
    const now = options.now ?? Date.now;
    const deadline = now() + (options.timeLimitMs ?? 1_200);
    const sequences = enumerateMaximalSequences(state, me, options.sequenceCap ?? 5_000);
    let best = null;
    let searched = 0;
    for (const sequence of sequences) {
        if (searched > 0 && now() >= deadline)
            break;
        const afterTurn = sequence.state.phase === 'gameOver' ? sequence.state : endTurn(sequence.state).state;
        const value = evaluate(sequence.state, me) - AI_WEIGHTS.opponentReply * opponentExpectation(afterTurn, me);
        searched += 1;
        if (!best || value > best.value || (value === best.value && rng() < 0.5))
            best = { sequence, value };
    }
    const chosen = best ?? { sequence: sequences[0], value: evaluate(state, me) };
    return { actions: chosen.sequence.actions, endTurn: true, evaluated: chosen.value, searched };
}
export function chooseAiActions(state, persona, rng, options) {
    if (state.phase !== 'actions')
        throw new Error('AI can only choose actions during the actions phase.');
    if (persona === 'penny')
        return choosePenny(state, rng);
    if (persona === 'nick')
        return chooseNick(state, rng);
    return chooseQuinn(state, rng, options);
}
export function seededRng(seed) {
    let value = seed >>> 0;
    return () => {
        value = (Math.imul(value, 1664525) + 1013904223) >>> 0;
        return value / 0x1_0000_0000;
    };
}
export function availableFlipRanks(state) {
    return [1, 2, 3, 4].filter((rank) => state.players[state.current].bank[rank] > 0);
}
export const AI_PERSONAS = {
    penny: { name: 'Penny', label: 'Easy', blurb: 'Plays on a whim' },
    nick: { name: 'Nick', label: 'Medium', blurb: 'Thinks it through' },
    quinn: { name: 'Quinn', label: 'Hard', blurb: 'Shows no mercy' },
};
export function regionName(state, regionId) {
    return MAPS[state.mapId].regions.find((region) => region.id === regionId)?.name ?? regionId;
}
//# sourceMappingURL=index.js.map